import React, { useEffect, useMemo, useState } from 'react';
import {
  Baby,
  Cake,
  ClockCounterClockwise,
  CopySimple,
  MagicWand,
  Printer,
  Sparkle,
  SpinnerGap,
  Trash,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { DashboardLayout } from '../../components/Layout';
import AIPremiumButton, { AI_USAGE_LUNA_TEXT } from '../../components/AIPremiumGate';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { useAuth } from '../../context/AuthContext';
import useEventTypeConfig from '../../hooks/useEventTypeConfig';
import { formatEventNameList } from '../../config/eventTypes';
import api from '../../services/api';
import './BabyShowerGameStudio.css';

const BABY_SHOWER_GAME_TYPES = [
  {
    value: 'parent_trivia',
    label: 'Parent Trivia',
    description: 'Questions about the parents, using only the facts you provide.',
  },
  {
    value: 'baby_bingo',
    label: 'Baby Bingo',
    description: 'A printable 5 × 5 activity card with a free center square.',
  },
  {
    value: 'prediction_cards',
    label: 'Prediction Cards',
    description: 'Sweet prompts for guests to predict and share wishes for the little one.',
  },
];

const BIRTHDAY_ACTIVITY_TYPES = [
  {
    value: 'celebrant_trivia',
    label: 'Celebrant Trivia',
    description: 'Questions about the celebrant, using only the facts you provide.',
  },
  {
    value: 'birthday_bingo',
    label: 'Birthday Bingo',
    description: 'A printable 5 × 5 card filled with party moments and a free centre square.',
  },
  {
    value: 'memory_cards',
    label: 'Memory Cards',
    description: 'Warm prompts that help guests share favourite memories and birthday wishes.',
  },
  {
    value: 'photo_challenge',
    label: 'Photo Challenge',
    description: 'A playful list of photo moments for guests to capture during the party.',
  },
];

const ALL_ACTIVITY_TYPES = [...BABY_SHOWER_GAME_TYPES, ...BIRTHDAY_ACTIVITY_TYPES];
const TYPE_LABELS = Object.fromEntries(ALL_ACTIVITY_TYPES.map((item) => [item.value, item.label]));
const isTrivia = (type) => ['parent_trivia', 'celebrant_trivia'].includes(type);
const isBingo = (type) => ['baby_bingo', 'birthday_bingo'].includes(type);

const defaultForm = {
  game_type: 'parent_trivia',
  host_names: '',
  personal_details: '',
  tone: 'playful',
  item_count: '10',
};

const printableText = (game) => {
  if (!game) return '';
  const content = game.content || {};
  if (isTrivia(game.game_type)) {
    return [
      content.title,
      content.instructions,
      ...(content.questions || []).flatMap((item, index) => [
        `${index + 1}. ${item.question}`,
        `Answer: ${item.answer}`,
      ]),
    ].join('\n');
  }
  if (game.game_type === 'baby_bingo') {
    return [content.title, content.instructions, ...(content.squares || [])].join('\n');
  }
  return [content.title, content.instructions, ...(content.prompts || []).map((item, index) => `${index + 1}. ${item}`)].join('\n');
};

function GamePreview({ game, isBirthday }) {
  const [showAnswers, setShowAnswers] = useState(false);
  const content = game?.content || {};

  useEffect(() => setShowAnswers(false), [game?.id]);

  if (!game) {
    return (
      <div className="game-studio-empty">
        {isBirthday ? <Cake className="h-10 w-10" weight="duotone" /> : <Baby className="h-10 w-10" weight="duotone" />}
        <h2>Your printable activity will appear here</h2>
        <p>Choose a format, add approved details, and generate a custom set.</p>
      </div>
    );
  }

  const bingoSquares = [...(content.squares || [])];
  bingoSquares.splice(12, 0, 'FREE');

  return (
    <div className="game-print-area">
      <div className="game-sheet__eyebrow">{isBirthday ? 'Celebrating' : 'A little celebration for'} {game.host_names}</div>
      <h2>{content.title}</h2>
      <p className="game-sheet__instructions">{content.instructions}</p>

      {isTrivia(game.game_type) && (
        <>
          <div className="game-trivia-list">
            {(content.questions || []).map((item, index) => (
              <div className="game-trivia-item" key={`${item.question}-${index}`}>
                <span>{index + 1}</span>
                <div>
                  <strong>{item.question}</strong>
                  <div className="game-answer-line" />
                </div>
              </div>
            ))}
          </div>
          <div className="game-answer-key game-studio-no-print">
            <Button type="button" variant="outline" size="sm" onClick={() => setShowAnswers((value) => !value)}>
              {showAnswers ? 'Hide answer key' : 'Show answer key'}
            </Button>
            {showAnswers && (
              <ol>
                {(content.questions || []).map((item, index) => <li key={`${item.answer}-${index}`}>{item.answer}</li>)}
              </ol>
            )}
          </div>
        </>
      )}

      {isBingo(game.game_type) && (
        <div className="game-bingo-grid">
          {bingoSquares.slice(0, 25).map((square, index) => (
            <div className={index === 12 ? 'is-free' : ''} key={`${square}-${index}`}>{square}</div>
          ))}
        </div>
      )}

      {!isTrivia(game.game_type) && !isBingo(game.game_type) && (
        <div className="game-prediction-list">
          {(content.prompts || []).map((prompt, index) => (
            <div key={`${prompt}-${index}`}>
              <span>{prompt}</span>
              <div className="game-answer-line" />
            </div>
          ))}
        </div>
      )}

      <div className="game-sheet__footer">Made with love for the {isBirthday ? 'birthday celebration' : 'baby shower'}</div>
    </div>
  );
}

export default function CelebrationActivityStudio() {
  const { currentEvent } = useAuth();
  const eventTypeConfig = useEventTypeConfig(currentEvent);
  const [form, setForm] = useState(defaultForm);
  const [games, setGames] = useState([]);
  const [activeGame, setActiveGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [deletingId, setDeletingId] = useState('');

  const eventId = currentEvent?.id;
  const isBirthday = eventTypeConfig.isBirthday;
  const gameTypes = isBirthday ? BIRTHDAY_ACTIVITY_TYPES : BABY_SHOWER_GAME_TYPES;
  const selectedGameType = useMemo(
    () => gameTypes.find((item) => item.value === form.game_type) || gameTypes[0],
    [form.game_type, gameTypes]
  );

  useEffect(() => {
    const names = isBirthday
      ? formatEventNameList(currentEvent?.honoree_names)
      : (currentEvent?.host_names || []).join(' & ');
    setForm((current) => ({
      ...current,
      game_type: isBirthday ? 'celebrant_trivia' : 'parent_trivia',
      host_names: names || currentEvent?.name || '',
    }));
  }, [currentEvent?.id, currentEvent?.host_names, currentEvent?.honoree_names, currentEvent?.name, isBirthday]);

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    const loadGames = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/events/${eventId}/celebration-games`);
        if (cancelled) return;
        setGames(response.data || []);
        setActiveGame((response.data || [])[0] || null);
      } catch (error) {
        if (!cancelled) toast.error(error.response?.data?.detail || 'Could not load saved games');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadGames();
    return () => { cancelled = true; };
  }, [eventId]);

  const updateForm = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const generateGame = async () => {
    if (!form.host_names.trim()) {
      toast.error(isBirthday ? 'Add the celebrant name first' : 'Add the parent or host names first');
      return;
    }
    try {
      setGenerating(true);
      const response = await api.post(`/events/${eventId}/celebration-games/generate`, {
        ...form,
        item_count: isBingo(form.game_type) ? 24 : Number(form.item_count),
      });
      setGames((current) => [response.data, ...current]);
      setActiveGame(response.data);
      toast.success(`${TYPE_LABELS[form.game_type]} generated`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not generate this game');
    } finally {
      setGenerating(false);
    }
  };

  const deleteGame = async (game) => {
    if (!window.confirm(`Delete “${game.content?.title || TYPE_LABELS[game.game_type]}”?`)) return;
    try {
      setDeletingId(game.id);
      await api.delete(`/events/${eventId}/celebration-games/${game.id}`);
      const remaining = games.filter((item) => item.id !== game.id);
      setGames(remaining);
      if (activeGame?.id === game.id) setActiveGame(remaining[0] || null);
      toast.success('Game set deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not delete the game set');
    } finally {
      setDeletingId('');
    }
  };

  const copyGame = async () => {
    if (!activeGame) return;
    try {
      await navigator.clipboard.writeText(printableText(activeGame));
      toast.success('Game copied');
    } catch (_error) {
      toast.error('Could not copy the game');
    }
  };

  const printGame = () => {
    document.body.classList.add('printing-game-studio');
    window.print();
    window.setTimeout(() => document.body.classList.remove('printing-game-studio'), 250);
  };

  return (
    <DashboardLayout>
      <main className="game-studio-page">
        <header className="game-studio-header game-studio-no-print">
          <div>
            <div className="game-studio-kicker"><Sparkle weight="fill" /> {eventTypeConfig.labels.celebrationStudioKicker}</div>
            <h1>{eventTypeConfig.labels.celebrationStudioTitle || eventTypeConfig.labels.gameStudioTitle}</h1>
            <p>Create personalized, printable activities using facts approved by the hosts.</p>
          </div>
          {activeGame && (
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={copyGame}><CopySimple /> Copy</Button>
              <Button type="button" onClick={printGame}><Printer /> Print cards</Button>
            </div>
          )}
        </header>

        <div className="game-studio-layout">
          <div className="game-studio-sidebar game-studio-no-print">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><MagicWand /> Customize a game</CardTitle>
                <CardDescription>{selectedGameType.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Game format</Label>
                  <Select value={form.game_type} onValueChange={(value) => updateForm('game_type', value)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {gameTypes.map((item) => <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="game-host-names">{isBirthday ? 'Celebrant names' : 'Parent or host names'}</Label>
                  <Input id="game-host-names" value={form.host_names} onChange={(event) => updateForm('host_names', event.target.value)} placeholder={isBirthday ? 'e.g., Aarav' : 'e.g., Aisha & Kabir'} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="game-personal-details">Verified fun facts</Label>
                  <Textarea
                    id="game-personal-details"
                    rows={7}
                    value={form.personal_details}
                    onChange={(event) => updateForm('personal_details', event.target.value)}
                    placeholder={isBirthday
                      ? 'One fact per line, for example:\nAarav loves space and cricket\nHis favourite cake is chocolate\nHe learned to ride a bicycle this year'
                      : 'One fact per line, for example:\nKabir chose the nursery colour\nAisha calls the baby Peanut\nTheir first trip together was Goa'}
                  />
                  <p className="text-xs leading-5 text-muted-foreground">Only include details the family is comfortable sharing. AI will not invent missing facts.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Tone</Label>
                    <Select value={form.tone} onValueChange={(value) => updateForm('tone', value)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="playful">Playful</SelectItem>
                        <SelectItem value="warm">Warm</SelectItem>
                        <SelectItem value="elegant">Elegant</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Items</Label>
                    <Select value={isBingo(form.game_type) ? '24' : form.item_count} disabled={isBingo(form.game_type)} onValueChange={(value) => updateForm('item_count', value)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[8, 10, 12, 16, 24].map((count) => <SelectItem key={count} value={String(count)}>{count}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <AIPremiumButton
                  aria-label="Generate personalized game"
                  featureName={isBirthday ? 'AI birthday activity customization' : 'AI baby shower game customization'}
                  usageCategory={AI_USAGE_LUNA_TEXT}
                  className="w-full"
                  onClick={generateGame}
                  disabled={generating}
                >
                  {generating ? <><SpinnerGap className="animate-spin" /> Creating game</> : <><MagicWand /> Generate personalized game</>}
                </AIPremiumButton>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base"><ClockCounterClockwise /> Saved games</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {loading && <p className="py-5 text-center text-sm text-muted-foreground">Loading saved games…</p>}
                {!loading && !games.length && <p className="py-5 text-center text-sm text-muted-foreground">No games generated yet.</p>}
                {games.map((game) => (
                  <div className={`game-history-item ${activeGame?.id === game.id ? 'is-active' : ''}`} key={game.id} data-testid={`saved-game-${game.id}`}>
                    <button type="button" onClick={() => setActiveGame(game)}>
                      <strong>{game.content?.title || TYPE_LABELS[game.game_type]}</strong>
                      <span>{TYPE_LABELS[game.game_type]} · {new Date(game.created_at).toLocaleDateString()}</span>
                    </button>
                    <Button type="button" variant="ghost" size="icon" onClick={() => deleteGame(game)} disabled={deletingId === game.id} aria-label="Delete saved game">
                      {deletingId === game.id ? <SpinnerGap className="animate-spin" /> : <Trash />}
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          <section className="game-studio-preview">
            <GamePreview game={activeGame} isBirthday={isBirthday} />
          </section>
        </div>
      </main>
    </DashboardLayout>
  );
}
